/**
 * Screen 10 — "Who does the work": the contractor roster (`/partner/vendors`).
 *
 * One `Roster` component, shown twice: inside onboarding step 8 and as the
 * Configure → Contractors page. It lists the roster (a table on wide screens,
 * cards at phone width, like the Leads inbox), adds and edits a contractor in
 * a dialog, disables and re-enables one, and imports a CSV.
 *
 * The CSV import always previews first: the file goes to the API as a dry
 * run, which answers with the same per-row verdicts a real import would
 * (spreadsheet row numbers, the field, the problem) and each valid row's
 * values as they will be stored. Only then does "Import" send it for real.
 * The server owns every rule (routes/partner/vendors.ts), so the preview can
 * never disagree with the import.
 *
 * Matching is route-lead's: trade × ZIP prefix × hours × tier. The
 * accept-deadline cascade and round-robin are package B2 and not here.
 */
import { useEffect, useId, useState, type ChangeEvent, type FormEvent } from 'react';
import { useSession } from '../auth/session';
import { can } from '../auth/roles';
import { ApiError } from '../api/errors';
import {
  CONTACT_RULES_ATTESTATION,
  RECONSENT_CHANNELS,
  VENDOR_TIERS,
  reconsentAttestation,
  type ReconsentChannel,
  type Vendor,
  type VendorImportResult,
  type VendorInput,
  type VendorLicense,
  type VendorTier,
} from '../api/types';
import { TRADES, tradeLabel } from '../config/model';
import { OnboardingPage } from '../setup/OnboardingPage';
import { Empty, ErrorNotice, Field, Loading, Modal, PageHead, Segmented, Switch, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

export const ROSTER_TEMPLATE = [
  'external_id,name,phone,email,trades,postal_prefixes,tier,hours,timezone,capacity_per_day,active,lat,lng,radius_miles,license_number,license_state,insurance_expires_at',
  'V-100,Rapid Rooter,+15550100001,dispatch@rapidrooter.example,plumbing,"981;980",preferred,"Mon-Fri 08:00-18:00; Sat 09:00-13:00",America/Los_Angeles,,yes,47.6062,-122.3321,15,RAPIDRR123AB,WA,2027-06-30',
  'V-101,Northwind HVAC,+15550100002,ops@northwind.example,"hvac;electrical",981,standard,24/7,,8,yes,,,,,,',
].join('\n');

/** Days before expiry that the roster starts warning. */
export const INSURANCE_WARN_DAYS = 30;

/** Today in the viewer's local calendar, as `YYYY-MM-DD`. */
function localToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * `expired` the day after the expiry date, `expiring` within
 * INSURANCE_WARN_DAYS of it, otherwise null (including no date on file).
 * Shown only: routing does not skip a contractor for it.
 */
export function insuranceStatus(expiresAt: string | null | undefined, now: Date = new Date()): 'expired' | 'expiring' | null {
  if (!expiresAt || !/^\d{4}-\d{2}-\d{2}$/.test(expiresAt)) return null;
  const today = localToday(now);
  if (expiresAt < today) return 'expired';
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const horizon = localToday(new Date(y, m - 1, d + INSURANCE_WARN_DAYS));
  return expiresAt <= horizon ? 'expiring' : null;
}

function InsuranceTag({ expiresAt }: { expiresAt: string | null | undefined }) {
  const status = insuranceStatus(expiresAt);
  if (status === 'expired') return <span className="tag bad" title={`Insurance expired ${expiresAt}`}>Insurance expired</span>;
  if (status === 'expiring') return <span className="tag warn" title={`Insurance expires ${expiresAt}`}>Insurance expires {expiresAt}</span>;
  return null;
}

const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(ROSTER_TEMPLATE)}`;

const TIER_LABEL: Record<string, string> = { preferred: 'Preferred', standard: 'Standard', overflow: 'Overflow' };
const TIER_TONE: Record<string, string> = { preferred: 'accent', standard: '', overflow: 'info' };

const US_TIMEZONES: Array<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
];

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}


function prefixSummary(prefixes: string[], max = 3): string {
  if (!prefixes.length) return '—';
  const shown = prefixes.slice(0, max).join(', ');
  return prefixes.length > max ? `${shown} +${prefixes.length - max}` : shown;
}

function StatusTags({ v }: { v: Vendor }) {
  return (
    <>
      {v.active ? (
        <span className="tag ok">Active</span>
      ) : v.pending_activation ? (
        <span className="tag warn">Waiting for activation · added by AI agent</span>
      ) : (
        <span className="tag">Disabled</span>
      )}
      {v.do_not_contact ? <span className="tag bad">Do not contact</span> : null}
      <InsuranceTag expiresAt={v.insurance_expires_at} />
    </>
  );
}

// ── Add / edit dialog ────────────────────────────────────────────────────────

interface Draft {
  name: string;
  external_id: string;
  phone: string;
  email: string;
  trades: string[];
  prefixes: string;
  tier: VendorTier;
  hours: string;
  timezone: string;
  capacity: string;
  active: boolean;
  do_not_contact: boolean;
  lat: string;
  lng: string;
  radius: string;
  license_number: string;
  license_state: string;
  insurance: string;
}

const numText = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n));
const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s.trim()));

function draftFrom(v: Vendor | null): Draft {
  return {
    name: v?.name ?? '',
    external_id: v?.external_id ?? '',
    phone: v?.phone ?? '',
    email: v?.email ?? '',
    trades: v?.trades ?? [],
    prefixes: (v?.postal_prefixes ?? []).join(', '),
    tier: (VENDOR_TIERS as readonly string[]).includes(v?.tier ?? '') ? (v?.tier as VendorTier) : 'standard',
    hours: v?.hours_text ?? '',
    timezone: v?.timezone ?? browserTimezone(),
    capacity: v?.capacity_per_day ? String(v.capacity_per_day) : '',
    active: v?.active ?? true,
    do_not_contact: v?.do_not_contact ?? false,
    lat: numText(v?.lat),
    lng: numText(v?.lng),
    radius: numText(v?.radius_miles),
    license_number: v?.license?.[0]?.number ?? '',
    license_state: v?.license?.[0]?.state ?? '',
    insurance: v?.insurance_expires_at ?? '',
  };
}

/**
 * The dialog edits the first license; any others (added through the API)
 * are kept as they are, since the PATCH replaces the whole list.
 */
function licenseInput(d: Draft, existing: Vendor | null): VendorLicense[] {
  const rest = (existing?.license ?? []).slice(1);
  const number = d.license_number.trim();
  const state = d.license_state.trim();
  if (!number && !state) return rest;
  const first = existing?.license?.[0];
  const keep = first && first.number === number && first.state.toUpperCase() === state.toUpperCase() ? first : {};
  return [{ ...keep, number, state }, ...rest];
}

function toInput(d: Draft, existing: Vendor | null = null): VendorInput {
  const hours = d.hours.trim();
  return {
    name: d.name.trim(),
    external_id: d.external_id.trim() || null,
    phone: d.phone.trim() || null,
    email: d.email.trim() || null,
    trades: d.trades,
    postal_prefixes: d.prefixes.split(/[,;|\n]+/).map((s) => s.trim()).filter(Boolean),
    tier: d.tier,
    hours: hours || null,
    timezone: hours ? d.timezone : null,
    capacity_per_day: d.capacity.trim() ? Number(d.capacity.trim()) : null,
    active: d.active,
    do_not_contact: d.do_not_contact,
    lat: numOrNull(d.lat),
    lng: numOrNull(d.lng),
    radius_miles: numOrNull(d.radius),
    license: licenseInput(d, existing),
    insurance_expires_at: d.insurance.trim() || null,
  };
}

export function VendorDialog({
  open,
  vendor,
  onClose,
  onSaved,
  onReconsent,
  onLinkSent,
}: {
  open: boolean;
  vendor: Vendor | null;
  onClose: () => void;
  onSaved: (v: Vendor, verb: string) => void;
  /** Opens the dialog that records the contractor's renewed consent (084). */
  onReconsent?: (v: Vendor) => void;
  onLinkSent?: (text: string) => void;
}) {
  const { api } = useSession();
  // 084: the contractor's own way back, sent to the email on file.
  const link = useMutation(async () => (vendor ? api.sendVendorResubscribeLink(vendor.id) : undefined));
  const [d, setD] = useState<Draft>(() => draftFrom(vendor));
  const formId = useId();
  useEffect(() => {
    if (open) setD(draftFrom(vendor));
  }, [open, vendor]);

  const save = useMutation(async () => (vendor ? api.updateVendor(vendor.id, toInput(d, vendor)) : api.createVendor(toInput(d))));
  // A contractor an AI agent prepared is activated only with the
  // contact-rules confirmation (the server refuses it otherwise).
  const [attested, setAttested] = useState(false);
  const needsAttestation = Boolean(vendor && !vendor.active && vendor.pending_activation);
  const toggle = useMutation(async () => {
    if (!vendor) return undefined;
    if (vendor.active) {
      await api.disableVendor(vendor.id);
      return { ...vendor, active: false };
    }
    return api.updateVendor(vendor.id, needsAttestation ? { active: true, contact_rules_attested: true } : { active: true });
  });
  useEffect(() => {
    if (open) {
      save.reset();
      toggle.reset();
      link.reset();
      setAttested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const issues = (field: string) => (save.error?.fields ?? []).filter((f) => f.path === field || f.path.startsWith(`${field}.`));
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setD((prev) => ({ ...prev, [key]: value }));
  const toggleTrade = (slug: string) =>
    set('trades', d.trades.includes(slug) ? d.trades.filter((t) => t !== slug) : TRADES.map((t) => t.slug).filter((t) => t === slug || d.trades.includes(t)));
  const ready = d.name.trim() !== '' && d.trades.length > 0 && d.prefixes.trim() !== '' && (d.phone.trim() !== '' || d.email.trim() !== '');
  const tzOptions = US_TIMEZONES.some((t) => t.value === d.timezone) ? US_TIMEZONES : [{ value: d.timezone, label: d.timezone }, ...US_TIMEZONES];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const saved = await save.run();
    if (saved) onSaved(saved, vendor ? 'Saved' : 'Added');
  };

  return (
    <Modal
      open={open}
      title={vendor ? `Edit ${vendor.name}` : 'Add a contractor'}
      onClose={onClose}
      footer={
        <>
          {vendor ? (
            <button
              type="button"
              className={`btn ${vendor.active ? 'btn-danger' : ''}`}
              style={{ marginRight: 'auto' }}
              disabled={toggle.pending || (needsAttestation && !attested)}
              onClick={async () => {
                const next = await toggle.run();
                if (next) onSaved(next, vendor.active ? 'Disabled' : 'Enabled');
              }}
            >
              {vendor.active ? 'Disable' : 'Enable'}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={save.pending || !ready}>
            {save.pending ? 'Saving…' : vendor ? 'Save' : 'Add contractor'}
          </button>
        </>
      }
    >
      {needsAttestation ? (
        <div className="notice warn stack" style={{ gap: 6 }}>
          <p className="small" style={{ margin: 0 }}>
            An AI agent added this contractor or changed its phone or email, so it receives no leads until you activate it.
          </p>
          <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }} htmlFor={`${formId}-attest`}>
            <input id={`${formId}-attest`} type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
            <span>{CONTACT_RULES_ATTESTATION}</span>
          </label>
        </div>
      ) : null}
      <form id={formId} className="stack" onSubmit={submit} noValidate>
        <div className="grid-2">
          <Field label="Name" issues={issues('name')}>
            {(p) => <input {...p} className="input" value={d.name} maxLength={200} onChange={(e) => set('name', e.target.value)} autoComplete="organization" />}
          </Field>
          <Field label="Your ID" optional hint="Your own reference. A CSV re-import matches on it." issues={issues('external_id')}>
            {(p) => <input {...p} className="input input-mono" value={d.external_id} maxLength={100} onChange={(e) => set('external_id', e.target.value)} />}
          </Field>
          <Field label="Phone" hint="Called from the widget’s call button." issues={issues('phone')}>
            {(p) => <input {...p} className="input" type="tel" inputMode="tel" value={d.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 010 0001" />}
          </Field>
          <Field label="Email" hint="Gets the lead brief link." issues={issues('email')}>
            {(p) => <input {...p} className="input" type="email" inputMode="email" value={d.email} onChange={(e) => set('email', e.target.value)} placeholder="dispatch@example.com" />}
          </Field>
        </div>
        <fieldset className="stack-sm">
          <legend className="label" style={{ marginBottom: 6 }}>
            Trades <span className="faint">· {d.trades.length} selected</span>
          </legend>
          <div className="chips">
            {TRADES.map((t) => (
              <button key={t.slug} type="button" className="chip" aria-pressed={d.trades.includes(t.slug)} onClick={() => toggleTrade(t.slug)}>
                {t.label}
              </button>
            ))}
          </div>
          {issues('trades').length ? (
            <p className="err small" role="alert">
              {issues('trades')[0]?.message}
            </p>
          ) : null}
        </fieldset>
        <Field label="ZIP codes or prefixes" hint="Comma separated. 981 covers every ZIP starting 981." issues={issues('postal_prefixes')}>
          {(p) => <input {...p} className="input input-mono" inputMode="numeric" value={d.prefixes} onChange={(e) => set('prefixes', e.target.value)} placeholder="981, 98052" />}
        </Field>
        <Segmented label="Tier" value={d.tier} onChange={(v) => set('tier', v)} options={VENDOR_TIERS.map((t) => ({ value: t, label: TIER_LABEL[t] ?? t }))} />
        <p className="small muted" style={{ marginTop: -8 }}>
          Leads go to preferred contractors first, then standard, then overflow.
        </p>
        <div className="grid-2">
          <Field label="Hours" optional hint="Blank means any time. Like: Mon-Fri 08:00-18:00; Sat 09:00-13:00" issues={issues('hours')}>
            {(p) => <input {...p} className="input" value={d.hours} onChange={(e) => set('hours', e.target.value)} placeholder="Mon-Fri 08:00-18:00" />}
          </Field>
          <Field label="Time zone" hint="The hours are read in this zone." issues={issues('timezone')}>
            {(p) => (
              <select {...p} className="input" value={d.timezone} disabled={!d.hours.trim()} onChange={(e) => set('timezone', e.target.value)}>
                {tzOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <Field label="Leads per day" optional hint="Blank means no limit." issues={issues('capacity_per_day')}>
          {(p) => <input {...p} className="input" type="number" min={1} max={10000} inputMode="numeric" value={d.capacity} onChange={(e) => set('capacity', e.target.value)} style={{ maxWidth: 160 }} />}
        </Field>
        <fieldset className="stack-sm">
          <legend className="label" style={{ marginBottom: 6 }}>
            Service area <span className="faint">· optional</span>
          </legend>
          <p className="small muted" style={{ margin: 0 }}>
            A centre and a radius, as well as the ZIP codes above. Leads are matched by ZIP code today; the radius is used once leads carry a location.
          </p>
          <div className="grid-3">
            <Field label="Latitude" issues={issues('lat')}>
              {(p) => <input {...p} className="input input-mono" inputMode="decimal" value={d.lat} onChange={(e) => set('lat', e.target.value)} placeholder="47.6062" />}
            </Field>
            <Field label="Longitude" issues={issues('lng')}>
              {(p) => <input {...p} className="input input-mono" inputMode="decimal" value={d.lng} onChange={(e) => set('lng', e.target.value)} placeholder="-122.3321" />}
            </Field>
            <Field label="Radius (miles)" issues={issues('radius_miles')}>
              {(p) => <input {...p} className="input" type="number" min={0.1} max={500} step={0.1} inputMode="decimal" value={d.radius} onChange={(e) => set('radius', e.target.value)} placeholder="15" />}
            </Field>
          </div>
        </fieldset>
        <div className="grid-3">
          <Field label="License number" optional issues={issues('license')}>
            {(p) => <input {...p} className="input input-mono" value={d.license_number} maxLength={50} onChange={(e) => set('license_number', e.target.value)} />}
          </Field>
          <Field label="License state" optional hint="Two letters, e.g. WA">
            {(p) => <input {...p} className="input" value={d.license_state} maxLength={2} autoCapitalize="characters" onChange={(e) => set('license_state', e.target.value.toUpperCase())} placeholder="WA" />}
          </Field>
          <Field label="Insurance expires" optional hint={`Flagged on the roster ${INSURANCE_WARN_DAYS} days ahead.`} issues={issues('insurance_expires_at')}>
            {(p) => <input {...p} className="input" type="date" value={d.insurance} onChange={(e) => set('insurance', e.target.value)} />}
          </Field>
        </div>
        {(vendor?.license ?? []).length > 1 ? (
          <p className="small muted" style={{ marginTop: -8 }}>
            Also on file, kept as they are: {(vendor?.license ?? []).slice(1).map((l) => `${l.state} ${l.number}`).join(', ')}.
          </p>
        ) : null}
        {vendor?.do_not_contact ? (
          <div className="notice warn stack-sm" role="group" aria-labelledby={`${formId}-dnc`}>
            <p id={`${formId}-dnc`} className="small" style={{ margin: 0 }}>
              <strong>Do not contact.</strong> {vendor.name} asked not to be contacted, so they are never offered a lead. Only they can lift it: record what they told you, or email them their own link to start offers again.
            </p>
            <div className="row" style={{ flexWrap: 'wrap' }}>
              <button type="button" className="btn" onClick={() => onReconsent?.(vendor)}>
                They asked for offers again…
              </button>
              {vendor.email ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={link.pending}
                  onClick={async () => {
                    const sent = await link.run();
                    if (sent) onLinkSent?.(`Emailed ${vendor.name} a link to start offers again (${sent.sent_to ?? 'their email'})`);
                  }}
                >
                  {link.pending ? 'Sending…' : 'Email them a re-subscribe link'}
                </button>
              ) : null}
            </div>
            {vendor.email ? (
              <p className="small muted" style={{ margin: 0 }}>
                Send the link only if they asked for it. It goes to {vendor.email}, and nothing changes unless they press its button.
              </p>
            ) : null}
            <ErrorNotice error={link.error} />
          </div>
        ) : vendor ? (
          <div>
            <Switch
              label="Do not contact"
              description="This contractor asked not to be contacted. They are never offered a lead, and a CSV import cannot turn this off. Once saved, only the contractor can lift it."
              checked={d.do_not_contact}
              onChange={(v) => set('do_not_contact', v)}
            />
          </div>
        ) : null}
        <ErrorNotice error={save.error?.code === 'VALIDATION_FAILED' || save.error?.code === 'INVALID_VENDOR' ? null : save.error} />
        {save.error && (save.error.code === 'VALIDATION_FAILED' || save.error.code === 'INVALID_VENDOR') ? (
          <p className="err small" role="alert">
            {save.error.message}
          </p>
        ) : null}
        <ErrorNotice error={toggle.error} />
      </form>
    </Modal>
  );
}

// ── Lifting an opt-out (migration 084) ──────────────────────────────────────

/** `YYYY-MM-DDTHH:MM` in the viewer's local time, for a datetime-local input. */
function localDateTimeValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Records that the CONTRACTOR told the partner they want offers again: how,
 * when and what they said, and the person confirms the sentence the server
 * stores ("<name> told us on <date> by phone that they want offers again").
 * There is no way to agree to offers for them.
 */
export function ReconsentDialog({ open, vendor, onClose, onSaved }: { open: boolean; vendor: Vendor | null; onClose: () => void; onSaved: (v: Vendor) => void }) {
  const { api } = useSession();
  const formId = useId();
  const [channel, setChannel] = useState<ReconsentChannel>('phone');
  const [when, setWhen] = useState(() => localDateTimeValue(new Date()));
  const [evidence, setEvidence] = useState('');
  const [attested, setAttested] = useState(false);
  const save = useMutation(async () =>
    vendor
      ? api.recordVendorReconsent(vendor.id, { channel, received_at: new Date(when).toISOString(), evidence: evidence.trim(), attested: true })
      : undefined,
  );
  useEffect(() => {
    if (open) {
      setChannel('phone');
      setWhen(localDateTimeValue(new Date()));
      setEvidence('');
      setAttested(false);
      save.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const at = when ? new Date(when) : null;
  const validDate = Boolean(at && !Number.isNaN(at.getTime()));
  const sentence = vendor && at && validDate ? reconsentAttestation(vendor.name, at, channel) : null;
  const ready = Boolean(sentence) && evidence.trim().length >= 10 && attested;
  const issues = (field: string) => (save.error?.fields ?? []).filter((f) => f.path === field);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    const saved = await save.run();
    if (saved) onSaved(saved);
  };

  return (
    <Modal
      open={open}
      title={vendor ? `${vendor.name} asked for offers again` : 'Lift an opt-out'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={save.pending || !ready}>
            {save.pending ? 'Saving…' : 'Lift the opt-out'}
          </button>
        </>
      }
    >
      <form id={formId} className="stack" onSubmit={submit} noValidate>
        <p className="small" style={{ margin: 0 }}>
          Record only what the contractor told you themselves. Nobody can agree to offers for them. This is kept as evidence, with your name.
        </p>
        <Field label="How did they tell you?" issues={issues('channel')}>
          {(p) => (
            <select {...p} className="input" value={channel} onChange={(e) => setChannel(e.target.value as ReconsentChannel)}>
              {RECONSENT_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="When?" hint="When they told you, in your time zone." issues={issues('received_at')}>
          {(p) => <input {...p} className="input" type="datetime-local" value={when} max={localDateTimeValue(new Date())} onChange={(e) => setWhen(e.target.value)} />}
        </Field>
        <Field label="What did they say?" hint="Their words, and who took it. For example: Called our dispatch line and asked to be sent jobs again. Taken by Sam." issues={issues('evidence')}>
          {(p) => <textarea {...p} className="input" rows={4} maxLength={2000} value={evidence} onChange={(e) => setEvidence(e.target.value)} />}
        </Field>
        {sentence ? (
          <label className="row small" style={{ gap: 8, alignItems: 'flex-start' }} htmlFor={`${formId}-attest`}>
            <input id={`${formId}-attest`} type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
            <span>I confirm: {sentence}</span>
          </label>
        ) : null}
        <ErrorNotice error={save.error} />
      </form>
    </Modal>
  );
}

// ── CSV import dialog ────────────────────────────────────────────────────────

function fileLevelDetail(error: ApiError): string | null {
  const b = error.body;
  if (Array.isArray(b.unknown_columns)) return `Columns we don’t read: ${b.unknown_columns.join(', ')}. Use: ${Array.isArray(b.allowed_columns) ? b.allowed_columns.join(', ') : 'the template’s columns'}.`;
  if (Array.isArray(b.missing_columns)) return `Missing columns: ${b.missing_columns.join(', ')}.`;
  if (typeof b.max_rows === 'number') return `The file has ${String(b.rows)} rows; the limit is ${b.max_rows}.`;
  if (typeof b.error === 'string' && b.error.includes(' ')) return `The file: ${b.error}.`;
  return null;
}

/** `File.text()` where it exists; FileReader otherwise (older Safari, jsdom). */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file);
  });
}

export function ImportDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: (r: VendorImportResult) => void }) {
  const { api } = useSession();
  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [readError, setReadError] = useState<string | null>(null);
  const preview = useMutation((text: string) => api.importVendors(text, true));
  const commit = useMutation((text: string) => api.importVendors(text, false));
  const [result, setResult] = useState<VendorImportResult | null>(null);
  const inputId = useId();

  useEffect(() => {
    if (!open) return;
    setCsv(null);
    setFileName('');
    setResult(null);
    setReadError(null);
    preview.reset();
    commit.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setResult(null);
    setReadError(null);
    setFileName(file.name);
    if (file.size > 1_000_000) {
      setCsv(null);
      setReadError('That file is over 1 MB. Split it and import each part.');
      return;
    }
    let text: string;
    try {
      text = await readText(file);
    } catch {
      setCsv(null);
      setReadError('We could not read that file. Save it as CSV and try again.');
      return;
    }
    setCsv(text);
    const res = await preview.run(text);
    if (res) setResult(res);
  };

  const errorsByRow = new Map<number, Array<{ field: string; message: string }>>();
  for (const err of result?.errors ?? []) errorsByRow.set(err.row, [...(errorsByRow.get(err.row) ?? []), err]);
  const valid = (result?.rows ?? []).filter((r) => r.action !== 'error');
  const SHOW = 50;

  return (
    <Modal
      open={open}
      title="Import contractors from CSV"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!csv || !result || result.accepted === 0 || commit.pending}
            onClick={async () => {
              if (!csv) return;
              const done = await commit.run(csv);
              if (done) onImported(done);
            }}
          >
            {commit.pending ? 'Importing…' : result && result.accepted > 0 ? `Import ${result.accepted} contractor${result.accepted === 1 ? '' : 's'}` : 'Import'}
          </button>
        </>
      }
    >
      <p className="small muted">
        One contractor per row. Rows that match a contractor already on your roster (by your ID, or by name) update it instead of adding a copy. Contractors missing from the file are left alone.
      </p>
      <div className="row">
        <label className="btn" htmlFor={inputId}>
          {fileName ? 'Choose another file' : 'Choose a CSV file'}
        </label>
        <input id={inputId} className="visually-hidden" type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={(e) => void choose(e)} />
        <a className="btn btn-ghost btn-sm" href={TEMPLATE_HREF} download="finderly-roster-template.csv">
          Download the CSV template
        </a>
      </div>
      {fileName ? <p className="small mono faint" style={{ overflowWrap: 'anywhere' }}>{fileName}</p> : null}
      {readError ? <div className="notice bad" role="alert">{readError}</div> : null}
      {preview.pending ? <p className="small muted" role="status">Checking the file…</p> : null}
      {preview.error ? (
        <div className="notice bad" role="alert">
          <div className="stack-sm" style={{ gap: 2 }}>
            <strong>{preview.error.message}</strong>
            {fileLevelDetail(preview.error) ? <span>{fileLevelDetail(preview.error)}</span> : null}
          </div>
        </div>
      ) : null}
      {result ? (
        <>
          <div className={`notice ${result.rejected ? 'warn' : 'ok'}`} role="status">
            <span>
              <strong>
                {result.accepted} ready
              </strong>{' '}
              ({result.created} new, {result.updated} update{result.updated === 1 ? '' : 's'})
              {result.rejected ? (
                <>
                  {' · '}
                  <strong>{result.rejected} need fixing</strong> and will be skipped. Fix them in your file and import it again.
                </>
              ) : null}
            </span>
          </div>
          {result.errors.length ? (
            <section aria-labelledby={`${inputId}-errs`} className="stack-sm">
              <h3 id={`${inputId}-errs`} className="label">
                Rows that need fixing
              </h3>
              <ul className="import-list">
                {[...errorsByRow.entries()].map(([row, errs]) => (
                  <li key={row}>
                    <span className="mono small faint">Row {row}</span>
                    <span className="small">
                      {errs.map((e, i) => (
                        <span key={i} style={{ display: 'block' }}>
                          {e.field ? <strong style={{ color: 'var(--ink)' }}>{e.field}: </strong> : null}
                          {e.message}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {valid.length ? (
            <section aria-labelledby={`${inputId}-ok`} className="stack-sm">
              <h3 id={`${inputId}-ok`} className="label">
                What will be imported
              </h3>
              <ul className="import-list">
                {valid.slice(0, SHOW).map((r) => (
                  <li key={r.row}>
                    <span className="mono small faint">Row {r.row}</span>
                    <span className="small stack-sm" style={{ gap: 2 }}>
                      <span>
                        <strong style={{ color: 'var(--ink)' }}>{r.vendor?.name ?? r.name}</strong>{' '}
                        <span className={`tag ${r.action === 'create' ? 'accent' : 'info'}`}>{r.action === 'create' ? 'new' : 'update'}</span>
                      </span>
                      {r.vendor ? (
                        <span className="muted">
                          {r.vendor.trades.map(tradeLabel).join(', ')} · ZIP {prefixSummary(r.vendor.postal_prefixes)} · {TIER_LABEL[r.vendor.tier] ?? r.vendor.tier}
                          {r.vendor.hours_text ? ` · ${r.vendor.hours_text}` : ' · any time'}
                          {r.vendor.radius_miles ? ` · ${r.vendor.radius_miles} mi radius` : ''}
                          {r.vendor.license?.length ? ` · License ${r.vendor.license.map((l) => `${l.state} ${l.number}`).join(', ')}` : ''}
                          {r.vendor.insurance_expires_at ? ` · Insured to ${r.vendor.insurance_expires_at}` : ''}
                        </span>
                      ) : null}
                      {r.vendor ? <InsuranceTag expiresAt={r.vendor.insurance_expires_at} /> : null}
                    </span>
                  </li>
                ))}
              </ul>
              {valid.length > SHOW ? <p className="small muted">and {valid.length - SHOW} more</p> : null}
            </section>
          ) : null}
        </>
      ) : null}
      <ErrorNotice error={commit.error} />
    </Modal>
  );
}

// ── The roster ───────────────────────────────────────────────────────────────

export function Roster({ compact }: { compact?: boolean }) {
  const { api, role } = useSession();
  const toast = useToast();
  const canEdit = can(role, 'roster');
  const [status, setStatus] = useState<'all' | 'true' | 'false'>('all');
  const [trade, setTrade] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const filters = { active: status, ...(trade ? { trade } : {}), ...(q ? { q } : {}) };
  const first = useQuery(() => api.listVendors(filters), [filters]);
  const [pages, setPages] = useState<Vendor[][]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const more = useMutation(async () => {
    if (!cursor) return null;
    const res = await api.listVendors(filters, cursor);
    setPages((p) => [...p, res.items]);
    setCursor(res.next_cursor);
    return res;
  });
  useEffect(() => {
    setPages([]);
    setCursor(first.data?.next_cursor ?? null);
  }, [first.data]);
  const items = [...(first.data?.items ?? []), ...pages.flat()];
  const filtered = status !== 'all' || trade !== '' || q !== '';

  const [editing, setEditing] = useState<Vendor | null>(null);
  const [dialog, setDialog] = useState<'none' | 'edit' | 'import' | 'reconsent'>('none');
  const openEdit = (v: Vendor | null) => {
    setEditing(v);
    setDialog('edit');
  };
  const done = (text: string) => {
    setDialog('none');
    toast(text);
    void first.reload();
  };

  // Bulk enable/disable (POST /vendors/bulk). The selection is cleared
  // whenever the list reloads, so it never holds rows the viewer can't see.
  // Cleared during render, not in an effect: an effect ran after the rows
  // were on screen and could drop a tick made before it flushed.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [selectionFor, setSelectionFor] = useState(first.data);
  if (selectionFor !== first.data) {
    setSelectionFor(first.data);
    setSelected(new Set());
  }
  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = items.length > 0 && items.every((v) => selected.has(v.id));
  // Enabling any contractor an AI agent prepared needs the contact-rules
  // confirmation (POST /vendors/bulk refuses it otherwise).
  const [bulkAttested, setBulkAttested] = useState(false);
  useEffect(() => setBulkAttested(false), [selected]);
  const selectedPending = items.filter((v) => selected.has(v.id) && !v.active && v.pending_activation).length;
  const bulk = useMutation(async (active: boolean) => {
    const res = await api.bulkSetVendorsActive([...selected], active, active && selectedPending > 0 && bulkAttested);
    toast(`${active ? 'Enabled' : 'Disabled'} ${res.updated} contractor${res.updated === 1 ? '' : 's'}${res.unchanged ? ` (${res.unchanged} already ${active ? 'active' : 'disabled'})` : ''}`);
    setSelected(new Set());
    void first.reload();
    return res;
  });
  const selectBox = (v: Vendor) => (
    <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggleSelected(v.id)} aria-label={`Select ${v.name}`} />
  );

  const actions = canEdit ? (
    <div className="row">
      <button type="button" className="btn btn-primary" onClick={() => openEdit(null)}>
        Add contractor
      </button>
      <button type="button" className="btn" onClick={() => setDialog('import')}>
        Import CSV
      </button>
      <a className="btn btn-ghost" href={TEMPLATE_HREF} download="finderly-roster-template.csv">
        Download the CSV template
      </a>
    </div>
  ) : null;

  return (
    <div className="stack">
      {actions}
      <section className="panel" aria-labelledby="roster-heading">
        {/* Keeps heading order h1 → h2 → h3 (the empty state is an h3). */}
        <h2 id="roster-heading" className="visually-hidden">Your contractors</h2>
        <div className="panel-head roster-filters">
          <label className="roster-search" htmlFor="roster-search">
            <span className="visually-hidden">Search contractors</span>
            <input id="roster-search" className="input" type="search" value={search} placeholder="Search name, ID or email" onChange={(e) => setSearch(e.target.value)} />
          </label>
          <label className="row small" style={{ gap: 6 }} htmlFor="roster-trade">
            <span className="label">Trade</span>
            <select id="roster-trade" className="input" style={{ minHeight: 30, width: 'auto' }} value={trade} onChange={(e) => setTrade(e.target.value)}>
              <option value="">All</option>
              {TRADES.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <Segmented
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: 'all', label: 'All' },
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Disabled' },
            ]}
          />
        </div>
        {first.loading && !first.data ? (
          <Loading label="Loading contractors" />
        ) : first.error ? (
          <div className="panel-body">
            <ErrorNotice error={first.error} />
          </div>
        ) : items.length ? (
          <>
            {canEdit && selected.size > 0 ? (
              <div className="panel-body row bulk-bar" role="region" aria-label="Selected contractors">
                <strong className="small">{selected.size} selected</strong>
                <button type="button" className="btn btn-sm btn-danger" disabled={bulk.pending} onClick={() => void bulk.run(false)}>
                  Disable
                </button>
                {selectedPending > 0 ? (
                  <label className="row small" style={{ gap: 6, alignItems: 'flex-start', flexBasis: '100%' }} htmlFor="roster-bulk-attest">
                    <input id="roster-bulk-attest" type="checkbox" checked={bulkAttested} onChange={(e) => setBulkAttested(e.target.checked)} />
                    <span>
                      {selectedPending} added by an AI agent. {CONTACT_RULES_ATTESTATION}
                    </span>
                  </label>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={bulk.pending || (selectedPending > 0 && !bulkAttested)}
                  onClick={() => void bulk.run(true)}
                >
                  Enable
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>
                  Clear
                </button>
                <ErrorNotice error={bulk.error} />
              </div>
            ) : null}
            <div className="table-wrap collapse">
              <table className="data">
                <caption className="visually-hidden">Contractors</caption>
                <thead>
                  <tr>
                    {canEdit ? (
                      <th scope="col" style={{ width: 32 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((v) => v.id)))}
                          aria-label="Select all contractors shown"
                        />
                      </th>
                    ) : null}
                    <th scope="col">Contractor</th>
                    <th scope="col">Trades</th>
                    <th scope="col">ZIP</th>
                    <th scope="col">Tier</th>
                    {compact ? null : <th scope="col">Hours</th>}
                    {compact ? null : <th scope="col">Contact</th>}
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((v) => (
                    <tr key={v.id} className={v.active ? undefined : 'dim'}>
                      {canEdit ? <td>{selectBox(v)}</td> : null}
                      <td>
                        {canEdit ? (
                          <button type="button" className="rowlink linkbtn" onClick={() => openEdit(v)} aria-label={`Edit ${v.name}`}>
                            {v.name}
                          </button>
                        ) : (
                          <strong style={{ color: 'var(--ink)' }}>{v.name}</strong>
                        )}
                        {v.external_id ? <div className="small faint mono">{v.external_id}</div> : null}
                      </td>
                      <td className="small">{v.trades.map(tradeLabel).join(', ')}</td>
                      <td className="small mono" title={v.postal_prefixes.join(', ')}>
                        {prefixSummary(v.postal_prefixes)}
                      </td>
                      <td>
                        <span className={`tag ${TIER_TONE[v.tier] ?? ''}`}>{TIER_LABEL[v.tier] ?? v.tier}</span>
                      </td>
                      {compact ? null : <td className="small">{v.hours_text ?? <span className="faint">Any time</span>}</td>}
                      {compact ? null : (
                        <td className="small">
                          {v.phone ? <div>{v.phone}</div> : null}
                          {v.email ? <div className="faint" style={{ overflowWrap: 'anywhere' }}>{v.email}</div> : null}
                        </td>
                      )}
                      <td>
                        <span className="row" style={{ gap: 4 }}>
                          <StatusTags v={v} />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cards">
              {items.map((v) => {
                const body = (
                  <>
                    <span className="row-between">
                      <strong style={{ color: 'var(--ink)' }}>{v.name}</strong>
                      <span className={`tag ${TIER_TONE[v.tier] ?? ''}`}>{TIER_LABEL[v.tier] ?? v.tier}</span>
                    </span>
                    <span className="small muted">
                      {v.trades.map(tradeLabel).join(', ')} · ZIP {prefixSummary(v.postal_prefixes)}
                    </span>
                    <span className="small muted">{v.hours_text ?? 'Any time'}</span>
                    <span className="row" style={{ gap: 6 }}>
                      <StatusTags v={v} />
                    </span>
                  </>
                );
                return canEdit ? (
                  // A checkbox cannot live inside the card's button, so the
                  // row holds both side by side.
                  <div key={v.id} className="card-row">
                    <label className="card-select">{selectBox(v)}</label>
                    <button type="button" className="card" onClick={() => openEdit(v)} aria-label={`Edit ${v.name}`}>
                      {body}
                    </button>
                  </div>
                ) : (
                  <div key={v.id} className="card">
                    {body}
                  </div>
                );
              })}
            </div>
            {cursor ? (
              <div className="panel-body" style={{ textAlign: 'center' }}>
                <button type="button" className="btn" disabled={more.pending} onClick={() => void more.run()}>
                  {more.pending ? 'Loading…' : 'Load more'}
                </button>
              </div>
            ) : null}
          </>
        ) : filtered ? (
          <Empty title="No contractors match">Try another trade, status or search.</Empty>
        ) : (
          <Empty title="No contractors yet">Add them one by one, or import your roster from a spreadsheet. Download the template to see the columns.</Empty>
        )}
      </section>
      {canEdit ? (
        <>
          <VendorDialog
            open={dialog === 'edit'}
            vendor={editing}
            onClose={() => setDialog('none')}
            onSaved={(v, verb) => done(`${verb} ${v.name}`)}
            onReconsent={(v) => {
              setEditing(v);
              setDialog('reconsent');
            }}
            onLinkSent={(text) => done(text)}
          />
          <ReconsentDialog
            open={dialog === 'reconsent'}
            vendor={editing}
            onClose={() => setDialog('none')}
            onSaved={(v) => done(`Lifted the opt-out for ${v.name}`)}
          />
          <ImportDialog
            open={dialog === 'import'}
            onClose={() => setDialog('none')}
            onImported={(r) => done(`Imported: ${r.created} added, ${r.updated} updated${r.rejected ? `, ${r.rejected} skipped` : ''}`)}
          />
        </>
      ) : null}
    </div>
  );
}

const LEDE = 'The pros who take your leads. Each lead goes to the best match by trade, ZIP, hours and tier.';

/** Onboarding step 8 (plan.md screen 10). */
export function Contractors() {
  return (
    <OnboardingPage slug="contractors" lede={`${LEDE} Skip this step if your own team handles every lead.`}>
      <Roster compact />
    </OnboardingPage>
  );
}

/** Configure → Contractors. */
export function ContractorsPage() {
  return (
    <div>
      <PageHead title="Contractors" lede={LEDE} />
      <Roster />
    </div>
  );
}
